import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppointmentEntity } from 'src/app/models/appointment.model';
import { BusinessInformationEntity } from 'src/app/models/business-information.model';
import { AppointmentService } from 'src/app/services/appointment/appointment.service';
import { BusinessService } from 'src/app/services/business/business.service';
import { UserProfileService } from 'src/app/services/user-profile/user-profile.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { AlertDialogConfig } from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import firebase from 'firebase/app';
import 'firebase/auth';
import { EMPTY, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

type CheckInStep = 'phone' | 'otp' | 'signup' | 'complete';

@Component({
  selector: 'app-checkin',
  templateUrl: './checkin.component.html',
  styleUrls: ['./checkin.component.css'],
})
export class CheckinComponent implements OnInit {
  storeId: string = '';
  currentStep: CheckInStep = 'phone';
  phoneForm: FormGroup;
  otpForm: FormGroup;
  signupForm: FormGroup;
  loading = false;
  loadingBusiness = false;
  error = '';
  successMessage = '';
  appointmentData: AppointmentEntity | null = null;
  businessData: BusinessInformationEntity | null = null;
  currentYear = new Date().getFullYear();
  pointsEarned: number = 0; // JPoints earned from check-in

  // Firebase phone authentication
  recaptchaVerifier: firebase.auth.RecaptchaVerifier | null = null;
  verifiedPhoneNumber: string = '';
  verificationId: string = '';
  firebaseUserId: string = ''; // Store Firebase user ID after verification

  // Alert dialog
  showAlert = false;
  alertConfig: AlertDialogConfig = {
    title: '',
    message: '',
    confirmText: 'OK',
    cancelText: 'Cancel',
    type: 'info',
    showCancel: true,
  };
  private pendingAppointmentRequest: {
    phoneNumber: string;
    userId: string;
  } | null = null;

  // App deep linking constants
  private readonly APP_OPEN_TIMEOUT = 3000; // 3 seconds
  private readonly DEEP_LINK_BASE = 'technology.jk.jujuja://stores';
  private readonly IOS_APP_STORE_URL = 'https://apps.apple.com/app/jujuja/id6553972212';
  private readonly ANDROID_APP_STORE_URL = 'https://play.google.com/store/apps/details?id=technology.jk.jujuja';
  constructor(
    private readonly appointmentService: AppointmentService,
    private readonly userProfileService: UserProfileService,
    private readonly businessService: BusinessService,
    private readonly authService: AuthService,
    private router: Router,
    private fb: FormBuilder,
    private route: ActivatedRoute
  ) {
    // Phone number form
    this.phoneForm = this.fb.group({
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)],
      ],
    });

    // OTP form
    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    // Signup form
    this.signupForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      phoneNumber: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    // Get storeId from query params (storeId or booking parameter)
    this.route.queryParams.subscribe((params) => {
      this.storeId = params['storeId'] || params['booking'] || '';
      if (!this.storeId) {
        this.error = 'Invalid QR code. Store ID is missing.';
      } else {
        this.loadingBusiness = true;
        this.businessService.findById(this.storeId).subscribe({
          next: (business) => {
            this.businessData = {
              ...business,
              createdTime: new Date(),
              modifiedTime: new Date(),
            };
            this.loadingBusiness = false;
          },
          error: (error) => {
            console.error('Error fetching business data:', error);
            this.loadingBusiness = false;
          },
        });
      }
    });

    // Initialize reCAPTCHA verifier after view loads
    setTimeout(() => {
      this.initializeRecaptcha();
    }, 1000);
  }

  private initializeRecaptcha(): void {
    try {
      if (!this.recaptchaVerifier) {
        this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
          'recaptcha-container',
          {
            size: 'invisible',
            callback: () => {
              console.log('reCAPTCHA verified');
            },
            'expired-callback': () => {
              console.log('reCAPTCHA expired');
              this.error = 'reCAPTCHA expired. Please try again.';
            },
          }
        );
      }
    } catch (error) {
      console.error('Error initializing reCAPTCHA:', error);
    }
  }

  // Format phone number as user types
  formatPhoneNumber(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 10) {
      value = value.substring(0, 10);
    }

    let formatted = '';
    if (value.length > 0) {
      formatted = '(' + value.substring(0, 3);
      if (value.length >= 4) {
        formatted += ') ' + value.substring(3, 6);
        if (value.length >= 7) {
          formatted += '-' + value.substring(6, 10);
        }
      } else if (value.length > 3) {
        formatted += value.substring(3);
      }
    }

    this.phoneForm.patchValue({ phoneNumber: formatted }, { emitEvent: false });
  }

  // Convert phone number to API format: +1XXXXXXXXXX
  private formatPhoneNumberForAPI(phoneNumber: string): string {
    const digits = phoneNumber.replace(/\D/g, '');
    return `+1${digits}`;
  }

  // Step 1: Submit phone number and send OTP
  async onPhoneSubmit(): Promise<void> {
    if (this.phoneForm.invalid) {
      this.error = 'Please enter a valid phone number';
      return;
    }

    if (!this.recaptchaVerifier) {
      this.error = 'reCAPTCHA not initialized. Please refresh and try again.';
      return;
    }

    this.loading = true;
    this.error = '';
    const phoneNumber = this.formatPhoneNumberForAPI(
      this.phoneForm.value.phoneNumber
    );

    try {
      // Send OTP via Firebase
      const confirmationResult = await this.authService.signInWithPhoneNumber(
        phoneNumber,
        this.recaptchaVerifier
      );

      console.log('OTP sent successfully');
      this.verifiedPhoneNumber = phoneNumber;
      this.verificationId = confirmationResult.verificationId;
      this.currentStep = 'otp';
      this.loading = false;
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      this.handlePhoneAuthError(error);
      this.loading = false;
      // Reset reCAPTCHA for retry
      this.recaptchaVerifier?.clear();
      this.recaptchaVerifier = null;
      this.initializeRecaptcha();
    }
  }

  // Handle phone authentication errors
  private handlePhoneAuthError(error: any): void {
    if (error.code === 'auth/invalid-phone-number') {
      this.error = 'Invalid phone number format';
    } else if (error.code === 'auth/too-many-requests') {
      this.error = 'Too many attempts. Please try again later.';
    } else if (error.code === 'auth/quota-exceeded') {
      this.error = 'SMS quota exceeded. Please try again later.';
    } else if (error.message?.includes('BILLING_NOT_ENABLED')) {
      this.error =
        'Phone verification is temporarily unavailable. Please contact support.';
    } else {
      this.error =
        error.message || 'Failed to send verification code. Please try again.';
    }
  }

  // Step 2: Verify OTP
  async onOtpSubmit(): Promise<void> {
    if (this.otpForm.invalid) {
      this.error = 'Please enter a valid 6-digit code';
      return;
    }

    if (!this.verificationId) {
      this.error = 'Please request a new verification code';
      return;
    }

    this.loading = true;
    this.error = '';
    const verificationCode = this.otpForm.value.otp;

    try {
      // Create credential and sign in
      const credential = firebase.auth.PhoneAuthProvider.credential(
        this.verificationId,
        verificationCode
      );

      const result = await firebase.auth().signInWithCredential(credential);
      console.log('Phone verified successfully:', result.user);

      // Check if account is disabled
      if (result.user?.uid) {
        // Store Firebase user ID for later use
        this.firebaseUserId = result.user.uid;

        // After verification, check if user profile exists in backend
        this.checkUserAndProceed(this.verifiedPhoneNumber);
      }
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      this.handleOtpVerificationError(error);
      this.loading = false;
    }
  }

  // Handle OTP verification errors
  private handleOtpVerificationError(error: any): void {
    if (error.code === 'auth/invalid-verification-code') {
      this.error = 'Invalid verification code. Please try again.';
    } else if (error.code === 'auth/code-expired') {
      this.error = 'Verification code expired. Please request a new code.';
    } else if (error.code === 'auth/user-disabled') {
      this.error =
        'Your account is scheduled for deletion. Please contact support at 713-346-3889.';
    } else {
      this.error = error.message || 'Failed to verify code. Please try again.';
    }
  }

  // Check if user exists and proceed with check-in or signup
  private checkUserAndProceed(phoneNumber: string): void {
    this.loading = true;

    this.userProfileService
      .checkUserExists(phoneNumber)
      .pipe(
        switchMap((user) =>
          this.ensureStoreUserProfile(user, phoneNumber).pipe(
            switchMap(() =>
              this.appointmentService
                .checkInAppointment(this.storeId, phoneNumber)
                .pipe(
                  tap((response) => this.handleCheckInSuccess(response)),
                  catchError((error) =>
                    this.handleCheckInFailure(error, phoneNumber, user.id)
                  )
                )
            )
          )
        ),
        catchError((error) => this.handleMissingUser(error, phoneNumber))
      )
      .subscribe();
  }

  private ensureStoreUserProfile(user: any, phoneNumber: string) {
    return this.userProfileService
      .findStoreUserProfile(phoneNumber, this.storeId)
      .pipe(
        switchMap((userProfile) => {
          if (userProfile) {
            return of(userProfile);
          }
          return this.userProfileService.createStoreUserProfile({
            phoneNumber,
            displayName: user.displayName,
            storeId: this.storeId,
          });
        }),
        catchError((error) => {
          console.error('Error finding store user profile:', error);
          return this.userProfileService
            .createStoreUserProfile({
              phoneNumber,
              displayName: user.displayName,
              storeId: this.storeId,
            })
            .pipe(
              tap((response) =>
                console.log(
                  'Store user profile created successfully:',
                  response
                )
              ),
              catchError((creationError) => {
                console.error(
                  'Error creating store user profile:',
                  creationError
                );
                this.loading = false;
                return EMPTY;
              })
            );
        })
      );
  }

  private handleCheckInSuccess(response: any): void {
    console.log('Appointment checked in successfully:', response);
    this.appointmentData = response.appointment;
    this.pointsEarned = response.pointsAdded || 0;
    this.currentStep = 'complete';
    this.successMessage =
      this.pointsEarned > 0
        ? `Congratulations! You've earned ${this.pointsEarned} JPoints for checking in!`
        : "You've successfully checked in for your appointment";
    this.loading = false;
  }

  private handleCheckInFailure(
    error: any,
    phoneNumber: string,
    userId: string
  ) {
    console.error('No appointment found:', error);
    this.loading = false;
    this.showNoAppointmentAlert(phoneNumber, userId);
    return EMPTY;
  }

  private handleMissingUser(error: any, phoneNumber: string) {
    console.error('User not found:', error);
    this.currentStep = 'signup';
    this.signupForm.patchValue({
      phoneNumber: this.phoneForm.value.phoneNumber,
    });
    this.loading = false;
    return EMPTY;
  }

  // Show alert when no appointment found
  private showNoAppointmentAlert(phoneNumber: string, userId: string): void {
    this.pendingAppointmentRequest = { phoneNumber, userId };
    this.alertConfig = {
      title: 'No Appointment Found',
      message: `You don\'t have an upcoming appointment at this store. Would you like to request for a new appointment at ${
        this.businessData?.storeName || 'this store'
      }?`,
      confirmText: 'Yes, Request Appointment',
      cancelText: 'No, Cancel',
      type: 'warning',
      showCancel: true,
    };
    this.showAlert = true;
  }

  // Handle alert confirmation
  onAlertConfirmed(confirmed: boolean): void {
    if (confirmed && this.pendingAppointmentRequest) {
      this.loading = true;
      this.error = '';
      const { phoneNumber, userId } = this.pendingAppointmentRequest;

      // Request new appointment
      this.appointmentService
        .requestAppointmentFromCheckIn(this.storeId, phoneNumber, userId)
        .subscribe({
          next: (response) => {
            console.log('Appointment request sent successfully:', response);
            this.currentStep = 'complete';
            this.successMessage =
              "Your appointment request has been sent. We'll contact you shortly!";
            this.loading = false;
          },
          error: (requestError) => {
            console.error('Error requesting appointment:', requestError);
            this.error =
              'Unable to request appointment. Please contact the store.';
            this.loading = false;
          },
        });
    } else {
      // User declined, show message
      this.error =
        'No appointment found. Please contact the store to book an appointment.';
    }

    this.pendingAppointmentRequest = null;
  }

  // Step 2: Submit signup
  async onSignupSubmit(): Promise<void> {
    if (this.signupForm.invalid) {
      this.error = 'Please fill in all required fields correctly';
      return;
    }

    this.loading = true;
    this.error = '';
    const formData = this.signupForm.value;
    const phoneNumber = this.formatPhoneNumberForAPI(formData.phoneNumber);

    // Create guest profile (pass Firebase user ID to avoid duplicate creation)
    this.userProfileService
      .createGuestProfile({
        phoneNumber,
        displayName: formData.fullName,
        email: formData.email,
        userId: this.firebaseUserId, // Pass existing Firebase user ID
      })
      .subscribe({
        next: (userResponse) => {
          console.log('Guest profile created successfully:', userResponse);

          // Create store user profile
          this.userProfileService
            .createStoreUserProfile({
              phoneNumber,
              displayName: formData.fullName,
              storeId: this.storeId,
            })
            .subscribe({
              next: (storeUserProfile) => {
                console.log(
                  'Store user profile created successfully:',
                  storeUserProfile
                );

                // Request appointment
                this.router.navigate(['/businesses', this.storeId]);
              },
              error: (error) => {
                console.error('Error creating store user profile:', error);
                this.error =
                  'Unable to create store profile. Please try again.';
                this.loading = false;
              },
            });
        },
        error: (error) => {
          console.error('Error creating guest profile:', error);
          this.error =
            error?.error?.message ||
            'Unable to create account. Please try again.';
          this.loading = false;
        },
      });
  }

  // Go back from OTP to phone entry
  goBackFromOtp(): void {
    this.currentStep = 'phone';
    this.error = '';
    this.otpForm.reset();
    this.verificationId = '';
    this.verifiedPhoneNumber = '';
    this.firebaseUserId = '';
  }

  // Resend OTP
  async resendOtp(): Promise<void> {
    this.otpForm.reset();
    this.error = '';
    this.verificationId = '';
    this.firebaseUserId = '';
    await this.onPhoneSubmit();
  }

  // Navigate to home/dashboard
  goToApp(): void {
    const userAgent = navigator.userAgent || navigator.vendor;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);

    if (!isIOS && !isAndroid) {
      // Desktop or unknown - show download options
      this.error = 'Please download the Jujuja app from your device app store';
      return;
    }

    this.attemptOpenApp();
  }

  private attemptOpenApp(): void {
    const userAgent = navigator.userAgent || navigator.vendor;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

    // Build deep link with storeId if available
    const deepLink = this.storeId 
      ? `${this.DEEP_LINK_BASE}?storeId=${this.storeId}`
      : this.DEEP_LINK_BASE;
    
    let appOpened = false;
    let timeoutId: any;
    const startTime = Date.now();
    let wasPageVisible = !document.hidden;

    // Track if page becomes hidden (indicates app opened)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page became hidden - app likely opened
        appOpened = true;
        clearTimeout(timeoutId);
      } else {
        // Page became visible again
        const elapsed = Date.now() - startTime;
        // If page becomes visible again quickly (< 500ms), app probably didn't open
        // But if enough time passed, app likely opened and user came back
        if (elapsed < 500 && wasPageVisible) {
          // Page became visible again quickly - app probably didn't open
          // But don't set appOpened to false, let timeout handle it
        }
      }
    };

    const handleBlur = () => {
      appOpened = true;
      clearTimeout(timeoutId);
    };

    const handlePageHide = () => {
      appOpened = true;
      clearTimeout(timeoutId);
    };

    // For Safari, also listen for focus events (when user returns to page)
    const handleFocus = () => {
      const elapsed = Date.now() - startTime;
      // If user returns to page after a delay, app likely opened
      if (elapsed > 1000) {
        appOpened = true;
        clearTimeout(timeoutId);
      }
    };

    // Add multiple event listeners for better detection
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', handlePageHide);
    if (isSafari) {
      window.addEventListener('focus', handleFocus);
    }

    if (isIOS) {
      this.openIOSApp(deepLink);
    } else if (isAndroid) {
      this.openAndroidApp(deepLink, userAgent);
    }

    // Set timeout to show download prompt if app doesn't open
    // Use shorter timeout for Safari since window.location.href is more immediate
    const timeout = isSafari ? 2500 : this.APP_OPEN_TIMEOUT;
    
    timeoutId = setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handlePageHide);
      if (isSafari) {
        window.removeEventListener('focus', handleFocus);
      }

      // Double check if page is still visible (app didn't open)
      if (!appOpened && !document.hidden) {
        // App didn't open - show download prompt
        if (confirm('App not installed. Would you like to download it?')) {
          this.downloadJujujaApp();
        }
      }
    }, timeout);
  }

  private openIOSApp(deepLink: string): void {
    // iOS - try multiple methods for better compatibility
    const userAgent = navigator.userAgent || navigator.vendor;
    const isChrome = /CriOS|Chrome/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

    if (isChrome) {
      // For Chrome on iOS, use window.location.href directly
      // Chrome on iOS handles this better than iframe
      window.location.href = deepLink;
    } else if (isSafari) {
      // For Safari, use window.location.href (works better than iframe in modern Safari)
      // Safari will attempt to open the app, and if it fails, we'll detect it via visibility change
      try {
        window.location.href = deepLink;
      } catch (e) {
        console.error('Error opening deep link in Safari:', e);
        // Fallback to iframe method if window.location fails
        this.openIOSAppWithIframe(deepLink);
      }
    } else {
      // For other browsers, try window.location first
      window.location.href = deepLink;
    }
  }

  private openIOSAppWithIframe(deepLink: string): void {
    // iOS - use iframe method as fallback
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    iframe.style.position = 'absolute';
    iframe.style.top = '-1000px';
    iframe.src = deepLink;
    document.body.appendChild(iframe);

    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 1000);
  }

  private openAndroidApp(deepLink: string, userAgent: string): void {
    const isChrome = /Chrome/.test(userAgent);

    if (isChrome) {
      // Android Chrome - use intent URL
      const intentUrl = this.storeId
        ? `intent://stores?storeId=${this.storeId}#Intent;scheme=technology.jk.jujuja;package=technology.jk.jujuja;end`
        : `intent://stores#Intent;scheme=technology.jk.jujuja;package=technology.jk.jujuja;end`;
      window.location.href = intentUrl;
    } else {
      // Other Android browsers
      window.location.href = deepLink;
    }
  }

  downloadJujujaApp(): void {
    const userAgent = navigator.userAgent || navigator.vendor;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);

    try {
      if (isIOS) {
        window.location.href = this.IOS_APP_STORE_URL;
      } else if (isAndroid) {
        window.location.href = this.ANDROID_APP_STORE_URL;
      } else {
        // Desktop - default to Android or show both options
        window.location.href = this.ANDROID_APP_STORE_URL;
      }
    } catch (error) {
      console.error('Error opening app store:', error);
      // Fallback: try window.open if window.location fails
      if (isIOS) {
        window.open(this.IOS_APP_STORE_URL, '_blank');
      } else {
        window.open(this.ANDROID_APP_STORE_URL, '_blank');
      }
    }
  }
}
